import Canvas from "@/components/Canvas/Canvas";
import CodePanel from "@/components/CodeEditors/CodePanel";
import LeftPanel from "@/components/Layout/LeftPanel";
import RightStyle from "@/components/RightStyle";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { ClassType } from "@/lib/tailwind/properties";
import { cn } from "@/lib/utils";
import { useActiveProject } from "@/utils/utils";
import {
  selectSelectedTempoElement,
  setIFrameChanged,
} from "@/store/iframeSlice";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import {
  StylingChange,
  StylingFramework,
} from "@/store/zustand/changeLedgerTypes";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore, {
  LayoutMode,
  LeftTab,
} from "@/store/zustand/layoutStore";
import { TempoElement } from "@/utils/tempoElement";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import "mousetrap/plugins/global-bind/mousetrap-global-bind";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPanelElement, getPanelGroupElement } from "react-resizable-panels";
import { toast } from "sonner";
import EngSpecTab from "../AI/EngSpec/EngSpec";
import { FileChangeDetails } from "../AI/EngSpec/FileChangeDetails";
import { useCodeDiffSetup } from "@/store/zustand/aiContextStore";
import PRDTab from "../AI/PRD/PRD";
import Console from "../Console";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import { debounce } from "lodash-es";

// Left panel are the tabs and the first panel next to it
const DEFAULT_LEFT_PANEL_SIZE = 19;
const LEFT_PANEL_AI_CHAT_SIZE = 22; // Chat needs a bit more space
const LEFT_PANEL_AI_CHAT_MIN_WIDTH = 320; // Minimum width to ensure chat panel has enough space for content

// Left extension is the panel that extends right next to the left panel
const DEFAULT_LEFT_PANEL_EXTENSION_SIZE = 35;
const LEFT_EXTENSION_PRD_SIZE = Math.round((100 - LEFT_PANEL_AI_CHAT_SIZE) / 2); // Should fill up half the screen past the left panel
const LEFT_EXTENSION_ENG_SPEC_SIZE = Math.round(
  (100 - LEFT_PANEL_AI_CHAT_SIZE) * 0.6
); // Should fill up 60% of the screen past the left panel

// Other two panels
const PRD_CANVAS_SIZE =
  100 - LEFT_EXTENSION_ENG_SPEC_SIZE - LEFT_PANEL_AI_CHAT_SIZE;
const DEFAULT_CANVAS_SIZE = 68;
const DEFAULT_RIGHT_SIZE = 0;
const RIGHT_OPEN_SIZE = 17;

const COMMUNITY_TAB_MIN_WIDTH = "520px"; // show two columns of community cards

const PanelsLayout = React.memo(() => {
  const dispatch = useDispatch<any>();

  // Setup code diff files when needed
  useCodeDiffSetup();

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup
  );
  const cssElementLookup = useCodebaseStore((state) => state.cssElementLookup);

  // Track the element that need to be highlighted/selected
  const selectedElement: TempoElement = useSelector(selectSelectedTempoElement);

  const executeCommand = useDesignCommandManager(
    (state) => state.executeCommand
  );

  // For the left nav
  const selectedTreeElement = (treeElementLookup || {})[
    selectedElement.codebaseId
  ];

  const project = useActiveProject();

  // Layout changes
  const leftExpanded = useLayoutStore((state) => state.leftExpanded);
  const expandLeft = useLayoutStore((state) => state.expandLeft);
  const rightExpanded = useLayoutStore((state) => state.rightExpanded);
  const expandRight = useLayoutStore((state) => state.expandRight);
  const updateSize = useLayoutStore((state) => state.updateSize);

  const layoutMode = useLayoutStore((state) => state.layoutMode);
  const leftTab = useLayoutStore((state) => state.leftTab);

  const leftPanelSize = useLayoutStore((state) => state.leftPanelSize);
  const leftPanelExtensionSize = useLayoutStore(
    (state) => state.leftPanelExtensionSize
  );
  const rightPanelSize = useLayoutStore((state) => state.rightPanelSize);

  const projectRef = useRef(project);
  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  const leftPanelRef = useRef(null);
  const leftPanelExtensionRef = useRef(null);
  const canvasPanelRef = useRef(null);
  const rightPanelRef = useRef(null);

  // Eng spec info
  const openedFileIsCommand = useAiContextStore(
    (state) => state.engSpecOpenedFileIsCommand
  );

  const aiChatEngSpecOpen = useAiContextStore(
    (state) => state.aiChatEngSpecOpen
  );

  // show transition only while hydrating
  const hasHydrated = useLayoutStore.persist.hasHydrated();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (hasHydrated) {
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    }
  }, [hasHydrated]);

  /**
   * Handle resizing of all panels based on the expected layout
   * right now it just re-sizes the panels to the default sizes
   */
  useEffect(() => {
    // Wait for all panels to load
    if (
      !leftPanelRef.current ||
      !leftPanelExtensionRef.current ||
      !canvasPanelRef.current ||
      !rightPanelRef.current
    ) {
      return;
    }

    const layoutArray: number[] = [];

    // Left panel
    let newLeftPanelSize: number;
    if (!leftExpanded) {
      newLeftPanelSize = 0;
    } else {
      let defaultCalculatedSize: number;
      if (
        leftTab === LeftTab.AIChat ||
        leftTab === LeftTab.EngSpec ||
        leftTab === LeftTab.SupabaseConnect ||
        leftTab === LeftTab.LocalEditing ||
        leftTab === LeftTab.Info ||
        leftTab === LeftTab.ProjectSettings
      ) {
        defaultCalculatedSize = LEFT_PANEL_AI_CHAT_SIZE;
      } else {
        defaultCalculatedSize = DEFAULT_LEFT_PANEL_SIZE;
      }
      newLeftPanelSize =
        leftPanelSize > 0 ? leftPanelSize : defaultCalculatedSize;
    }
    layoutArray.push(newLeftPanelSize);

    // Left panel extension - doesn't exist in design mode
    let newLeftPanelExtensionSize: number;
    if (
      !leftExpanded ||
      layoutMode === LayoutMode.Design ||
      leftTab === LeftTab.SupabaseConnect ||
      leftTab === LeftTab.LocalEditing ||
      leftTab === LeftTab.Info
    ) {
      newLeftPanelExtensionSize = 0;
    } else {
      let defaultCalculatedSize: number;
      if (layoutMode === LayoutMode.PRD) {
        defaultCalculatedSize = LEFT_EXTENSION_PRD_SIZE;
      } else if (leftTab === LeftTab.EngSpec) {
        defaultCalculatedSize = LEFT_EXTENSION_ENG_SPEC_SIZE;
      } else {
        defaultCalculatedSize = DEFAULT_LEFT_PANEL_EXTENSION_SIZE;
      }
      newLeftPanelExtensionSize =
        leftPanelExtensionSize > 0
          ? leftPanelExtensionSize
          : defaultCalculatedSize;
    }
    layoutArray.push(newLeftPanelExtensionSize);

    // Right panel
    let newRightPanelSize: number;
    if (!rightExpanded || layoutMode !== LayoutMode.Design) {
      newRightPanelSize = DEFAULT_RIGHT_SIZE; // Which is 0
    } else {
      // Only one "open" size for right panel, so if stored is > 0, use it, else default.
      newRightPanelSize = rightPanelSize > 0 ? rightPanelSize : RIGHT_OPEN_SIZE;
    }
    layoutArray.push(newRightPanelSize);

    // Canvas panel -> calculated last because it is whatever is leftover
    const sumOfOtherPanels = layoutArray.reduce((a, b) => a + b, 0);
    layoutArray.push(Math.max(0, 100 - sumOfOtherPanels)); // Ensure canvas size is not negative

    // @ts-expect-error TS2339
    leftPanelRef.current.resize(layoutArray[0]);
    // @ts-expect-error TS2339
    leftPanelExtensionRef.current.resize(layoutArray[1]);
    // @ts-expect-error TS2339
    canvasPanelRef.current.resize(layoutArray[3]); // Note this is a different order from the order of the panels themselves
    // @ts-expect-error TS2339
    rightPanelRef.current.resize(layoutArray[2]);
  }, [
    leftExpanded,
    rightExpanded,
    layoutMode,
    leftTab,
    leftPanelSize,
    leftPanelExtensionSize,
    rightPanelSize,
    hasHydrated,
  ]);

  const rawHandleLayoutChange = useCallback(
    (layout: number[]) => {
      if (layout.length !== 4) {
        return;
      }

      // Important: The ResizablePanelGroup's onLayout provides [left, leftExt, canvas, right]
      const newPanelSizes = {
        leftPanelSize: layout[0],
        leftPanelExtensionSize: layout[1],
        canvasSize: layout[2],
        rightPanelSize: layout[3],
      };

      updateSize("leftPanelSize", newPanelSizes.leftPanelSize);
      updateSize(
        "leftPanelExtensionSize",
        newPanelSizes.leftPanelExtensionSize
      );
      updateSize("canvasSize", newPanelSizes.canvasSize);
      updateSize("rightPanelSize", newPanelSizes.rightPanelSize);
    },
    [updateSize]
  );

  const throttledHandleLayoutChange = useMemo(
    () =>
      debounce(rawHandleLayoutChange, 1000, { leading: false, trailing: true }),
    [rawHandleLayoutChange]
  );

  useEffect(() => {
    const overallWidth = getPanelGroupElement(
      "overall-panel-group"
    )?.clientWidth;

    if (!overallWidth) {
      // If overallWidth is not available, don't try to calculate initial sizes based on it.
      // Persisted values (or defaults from the other useEffect) will take precedence.
      return;
    }

    // On first load, set the sizes IF they are not already set by persistence (i.e., are 0)
    if (leftPanelSize === 0) {
      const calculatedSize =
        ((getPanelElement("left-panel")?.clientWidth || 0) / overallWidth) *
        100;
      if (calculatedSize > 0) updateSize("leftPanelSize", calculatedSize);
    }
    if (leftPanelExtensionSize === 0) {
      const calculatedSize =
        ((getPanelElement("left-panel-extension")?.clientWidth || 0) /
          overallWidth) *
        100;
      if (calculatedSize > 0)
        updateSize("leftPanelExtensionSize", calculatedSize);
    }
    // Canvas size is derivative, usually not set directly here unless others are 0.
    if (rightPanelSize === 0) {
      const calculatedSize =
        ((getPanelElement("right-panel")?.clientWidth || 0) / overallWidth) *
        100;
      if (calculatedSize > 0) updateSize("rightPanelSize", calculatedSize);
    }
  }, []);

  const applyChangeToElementCallback = useCallback(
    (
      stylingChanges: { [styleKey: string]: string },
      modifiers: string[],
      customProperties: ClassType[]
    ) => {
      if (!selectedElement.isKnownElement()) {
        toast.error("Cannot find element in codebase");
        return;
      }

      const stylingChange = new StylingChange({
        codebaseId: selectedElement.codebaseId,
        stylingChanges,
        stylingFramework: StylingFramework.TAILWIND,
        modifiers,
        customProperties,
      });

      executeCommand(stylingChange);
      dispatch(setIFrameChanged());
    },
    [selectedElement]
  );

  const getLeftExtensionPanel = () => {
    return (
      <>
        <div
          className={cn(
            "w-full h-full",
            layoutMode !== LayoutMode.PRD && "hidden"
          )}
        >
          <PRDTab />
        </div>

        {/* EngSpec content */}
        <div
          className={cn(
            "w-full h-full",
            (leftTab !== LeftTab.EngSpec || layoutMode === LayoutMode.PRD) &&
              "hidden"
          )}
        >
          <EngSpecTab />
        </div>

        <div
          className={cn(
            "w-full h-full",
            (leftTab === LeftTab.EngSpec || layoutMode === LayoutMode.PRD) &&
              "hidden"
          )}
        >
          <ResizablePanelGroup direction="vertical">
            {/* Otherwise the code panel + console are shown */}
            <ResizablePanel defaultSize={70}>
              <CodePanel />
            </ResizablePanel>
            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={30}>
              {/* Always connect so that there is one console tracking errors */}
              <Console connect={true} />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </>
    );
  };

  const getCanvasPanel = () => {
    // For eng spec mode, there are different views
    const inEngSpecMode =
      layoutMode === LayoutMode.Code && leftTab === LeftTab.EngSpec;

    // Show the file details
    const showFileDetails = inEngSpecMode && aiChatEngSpecOpen;

    // Show the terminal along with file details
    const showTerminalForEngSpec =
      showFileDetails &&
      openedFileIsCommand &&
      layoutMode === LayoutMode.Code &&
      leftTab === LeftTab.EngSpec;

    return (
      <div className="flex flex-col h-full">
        <div className={cn("flex grow", showFileDetails && "hidden")}>
          <div className="middle-container relative flex flex-col h-full">
            <Canvas />
          </div>
        </div>
        <div
          className={cn(
            "w-full flex flex-col max-w-full grow",
            !showFileDetails && "hidden"
          )}
        >
          <ResizablePanelGroup direction="vertical">
            <ResizablePanel defaultSize={50} className="flex grow">
              <FileChangeDetails />
            </ResizablePanel>

            <ResizableHandle
              withHandle
              className={cn(!showTerminalForEngSpec && "hidden")}
            />

            <ResizablePanel
              defaultSize={50}
              className={cn(!showTerminalForEngSpec && "hidden")}
            >
              <Console connect={showTerminalForEngSpec && showFileDetails} />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full w-full flex-col">
      <ResizablePanelGroup
        id="overall-panel-group"
        direction="horizontal"
        onLayout={throttledHandleLayoutChange}
      >
        {/* Left Panel */}
        <ResizablePanel
          id="left-panel"
          ref={leftPanelRef}
          defaultSize={LEFT_PANEL_AI_CHAT_SIZE}
          className={isLoading ? "transition-all duration-100" : ""}
          style={
            leftTab === LeftTab.Community
              ? { minWidth: COMMUNITY_TAB_MIN_WIDTH }
              : { minWidth: LEFT_PANEL_AI_CHAT_MIN_WIDTH }
          }
        >
          <div className="h-full z-[10000]">
            <LeftPanel />
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle />

        {/* Left Panel Extension */}
        <ResizablePanel
          id="left-panel-extension"
          className={cn(
            "z-[10000] transition",
            isLoading ? "transition-all duration-100" : "",
            (layoutMode === LayoutMode.Design ||
              leftTab === LeftTab.SupabaseConnect ||
              leftTab === LeftTab.LocalEditing ||
              leftTab === LeftTab.Info) &&
              "hidden"
          )}
          ref={leftPanelExtensionRef}
          defaultSize={LEFT_EXTENSION_ENG_SPEC_SIZE}
        >
          {getLeftExtensionPanel()}
        </ResizablePanel>
        <ResizableHandle
          withHandle
          className={cn(
            (layoutMode === LayoutMode.Design ||
              leftTab === LeftTab.SupabaseConnect ||
              leftTab === LeftTab.LocalEditing ||
              leftTab === LeftTab.Info) &&
              "hidden"
          )}
        />

        {/* Canvas Panel */}
        <ResizablePanel
          id={"canvas-panel"}
          ref={canvasPanelRef}
          defaultSize={PRD_CANVAS_SIZE}
        >
          {getCanvasPanel()}
        </ResizablePanel>
        <ResizableHandle
          withHandle
          className={cn(layoutMode !== LayoutMode.Design && "hidden")}
        />

        {/* Right Panel */}
        <ResizablePanel
          id="right-panel"
          className={cn(
            "z-[1002]",
            layoutMode !== LayoutMode.Design && "hidden"
          )}
          ref={rightPanelRef}
          defaultSize={DEFAULT_RIGHT_SIZE}
        >
          <RightStyle
            selectedTreeElement={selectedTreeElement}
            cssElementLookup={cssElementLookup}
            applyChangeToElement={applyChangeToElementCallback}
          />
        </ResizablePanel>
      </ResizablePanelGroup>

      {!leftExpanded && (
        <button
          className="absolute top-1/2 left-0 transform -translate-y-1/2 bg-secondary-300 text-white p-2 rounded-r-md hover:bg-secondary-400 z-[100002]"
          onClick={() => expandLeft()}
        >
          <ChevronsRight className="w-4 h-4" />
        </button>
      )}

      {!rightExpanded && layoutMode === LayoutMode.Design && (
        <button
          className="absolute top-1/2 right-0 transform -translate-y-1/2 bg-secondary-300 text-white p-2 rounded-l-md hover:bg-secondary-400 z-[100002]"
          onClick={() => expandRight()}
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>
      )}
    </div>
  );
});

export default PanelsLayout;
